// src/stores/betSlipStore.js

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import _ from 'lodash'
import { isFixtureExpired } from '@/utils/expiry'

export const useBetSlipStore = defineStore(
  'betSlip',
  () => {
    // State - Initialize from localStorage
    const bets = ref(
      (() => {
        try {
          const stored = JSON.parse(localStorage.getItem('betSlip-bets')) || []
          // Ensure each bet has required fields
          stored.forEach((bet) => {
            if (!bet.status_name) {
              console.warn(
                `Loaded bet without 'status_name' for odd ID: ${bet.outcome_id}. Setting default status.`,
              )
              bet.status_name = 'unknown'
            }
          })
          return stored
        } catch (e) {
          console.error('Failed to parse bets from localStorage:', e)
          return []
        }
      })(),
    )

    const stake = ref(100)
    const acceptOddsChanges = ref(true)
    const bookingCode = ref(localStorage.getItem('betSlipCodeNumber') || '')
    const betId = ref(localStorage.getItem('betId') || '')

    // Computed Properties
    const betCount = computed(() => bets.value.length)

    const totalOdds = computed(() => {
      if (bets.value.length === 0) return '0.00'
      const total = bets.value.reduce((acc, bet) => acc * parseFloat(bet.odds || 1), 1)
      return total.toFixed(2)
    })

    const potentialWin = computed(() => {
      return stake.value * parseFloat(totalOdds.value)
    })

    const hasBets = computed(() => bets.value.length > 0)

    // Check if a specific bet is in the slip
    const isBetInSlip = computed(() => (matchId, outcomeId) => {
      return bets.value.some((bet) => bet.match_id === matchId && bet.outcome_id === outcomeId)
    })

    // Get selected odd for a match
    const getSelectedOdd = (matchId) => {
      return bets.value.find((bet) => bet.match_id === matchId) || null
    }

    // Get team names from match_name
    const getTeamNames = (matchId) => {
      const bet = bets.value.find((b) => b.match_id === matchId)
      if (bet && bet.match_name) {
        const teams = bet.match_name.split(' VS ')
        if (teams.length === 2) {
          return {
            homeTeam: teams[0],
            awayTeam: teams[1],
          }
        }
      }
      return {
        homeTeam: '',
        awayTeam: '',
      }
    }

    // Get bets grouped by live/prematch
    const liveBets = computed(() => {
      return bets.value.filter((bet) => bet.is_live === true || bet.live === 'l')
    })

    const prematchBets = computed(() => {
      return bets.value.filter((bet) => bet.is_live !== true && bet.live !== 'l')
    })

    // Actions
    const addBet = (bet) => {
      if (!bet || !bet.outcome_id) {
        console.error('Invalid bet or missing outcome_id:', bet)
        return
      }

      // Warn if 'status_name' missing
      if (!bet.status_name) {
        console.warn(
          `Adding bet without 'status_name' for odd ID: ${bet.outcome_id}. Setting default status.`,
        )
        bet.status_name = 'unknown'
      }

      // Check if this exact outcome is already selected (toggle off)
      const existingIndex = bets.value.findIndex(
        (b) => b.match_id === bet.match_id && b.outcome_id === bet.outcome_id,
      )

      if (existingIndex !== -1) {
        // Remove the bet (toggle off)
        bets.value.splice(existingIndex, 1)
      } else {
        // Remove any other bet from the same match
        bets.value = bets.value.filter((b) => b.match_id !== bet.match_id)

        // Add the new bet
        bets.value.push({
          ...bet,
          addedAt: Date.now(),
        })
      }

      saveToLocalStorageDebounced()
    }

    const removeBet = (outcomeId) => {
      bets.value = bets.value.filter((bet) => bet.outcome_id !== outcomeId)
      saveToLocalStorageDebounced()
    }

    const removeBetByMatchId = (matchId) => {
      const bet = bets.value.find((b) => b.match_id === matchId)
      if (bet) {
        bets.value = bets.value.filter((b) => b.match_id !== matchId)
        saveToLocalStorageDebounced()
      } else {
        console.warn(`No bet found for match ID: ${matchId}`)
      }
    }

    const clearBets = () => {
      bets.value = []
      try {
        localStorage.removeItem('betSlip-bets')
      } catch (e) {
        console.error('Failed to remove bets from localStorage:', e)
      }
      saveToLocalStorage()
      console.log('All bets cleared and persisted.')
    }

    const updateBetOdds = (matchId, newOdds, newOutcomeName, newAlias, newStatusName) => {
      const bet = bets.value.find((b) => b.match_id === matchId)
      if (!bet) return

      bet.odds = newOdds
      if (newOutcomeName) bet.outcome_name = newOutcomeName
      if (newAlias) bet.alias = newAlias
      if (newStatusName) bet.status_name = newStatusName
      bet.oddsChanged = true

      saveToLocalStorageDebounced()
    }

    const setStake = (amount) => {
      stake.value = Number(amount) || 0
    }

    const setAcceptOddsChanges = (value) => {
      acceptOddsChanges.value = value
    }

    const setBookingCode = (code) => {
      bookingCode.value = code
      localStorage.setItem('betSlipCodeNumber', code)
    }

    const clearBookingCode = () => {
      bookingCode.value = ''
      localStorage.removeItem('betSlipCodeNumber')
    }

    const generateBookingCode = () => {
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      const numbers = '0123456789'
      let result = ''
      for (let i = 0; i < 5; i++) {
        result += letters.charAt(Math.floor(Math.random() * letters.length))
      }
      for (let i = 0; i < 2; i++) {
        result += numbers.charAt(Math.floor(Math.random() * numbers.length))
      }
      setBookingCode(result)
      return result
    }

    const setBetId = (id) => {
      betId.value = id
      localStorage.setItem('betId', id)
    }

    const clearBetId = () => {
      betId.value = ''
      localStorage.removeItem('betId')
    }

    const removeExpiredBets = () => {
      // Create a copy of the bets array to avoid mutation issues during iteration
      const betsToCheck = [...bets.value]

      betsToCheck.forEach((bet) => {
        // Use the improved expiry logic from utils/expiry.js
        if (isFixtureExpired(bet)) {
          // Use removeBet to properly remove the bet
          const index = bets.value.findIndex((b) => b.outcome_id === bet.outcome_id)
          if (index !== -1) {
            bets.value.splice(index, 1)
            console.log(`Removed expired bet for match ID: ${bet.match_id}`)
          }
        }
      })

      saveToLocalStorageDebounced()
    }

    // Calculate taxes and winnings
    const calculateTaxesAndWinnings = (stakeAmount, totalOddsValue) => {
      const EXCISE_TAX_PERCENT = 15
      const WITHHOLDING_TAX_PERCENT = 20
      const MAX_PAYOUT = 1_000_000

      // 1) Excise (inclusive rate)
      const rawWagerTax = stakeAmount - (stakeAmount * 100) / (100 + EXCISE_TAX_PERCENT)
      const wagerTax = parseFloat(rawWagerTax.toFixed(4))
      const stakeAfterTax = parseFloat((stakeAmount - wagerTax).toFixed(4))

      const winning = parseFloat((stakeAfterTax * totalOddsValue).toFixed(2))

      // 3) Withholding on profit (inclusive rate)
      const rawProfit = winning - stakeAfterTax
      const rawWithholdingTax = rawProfit - (rawProfit * 100) / (100 + WITHHOLDING_TAX_PERCENT)
      const withholdingTax = parseFloat(rawWithholdingTax.toFixed(4))

      const possibleWin = parseFloat((winning - withholdingTax).toFixed(4))
      const payout = Math.min(possibleWin, MAX_PAYOUT)

      return {
        wagerTax,
        stakeAfterTax,
        withholdingTax,
        payout,
        winning,
      }
    }

    // Debounced save to localStorage
    const saveToLocalStorageDebounced = _.debounce(() => {
      try {
        localStorage.setItem('betSlip-bets', JSON.stringify(bets.value))
      } catch (e) {
        console.error('Failed to save bets to localStorage:', e)
      }
    }, 300)

    // Immediate save to localStorage
    const saveToLocalStorage = () => {
      try {
        localStorage.setItem('betSlip-bets', JSON.stringify(bets.value))
      } catch (e) {
        console.error('Failed to save bets to localStorage:', e)
      }
    }

    return {
      // State
      bets,
      stake,
      acceptOddsChanges,
      bookingCode,
      betId,

      // Computed
      betCount,
      totalOdds,
      potentialWin,
      hasBets,
      isBetInSlip,
      liveBets,
      prematchBets,

      // Actions
      addBet,
      removeBet,
      removeBetByMatchId,
      clearBets,
      updateBetOdds,
      setStake,
      setAcceptOddsChanges,
      setBookingCode,
      clearBookingCode,
      generateBookingCode,
      removeExpiredBets,
      getSelectedOdd,
      getTeamNames,
      calculateTaxesAndWinnings,
      setBetId,
      clearBetId,
      saveToLocalStorage,
    }
  },
  {
    persist: {
      paths: ['bets', 'stake', 'acceptOddsChanges', 'bookingCode'],
      storage: localStorage,
    },
  },
)
